import { isEqual, omit } from "es-toolkit";
import { toJS } from "mobx";

import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { isSupSubNode } from "@/models/utils/semanticChecks";

import type { CapturesMap, MatchResult } from "./types";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson, SwitchNotationVariant } from "@corca/data";

/**
 * Checks whether some nodes look like a given notation, and if so,
 * pulls out what filled its blanks.
 *
 * A notation is a small template: some fixed parts (like the `/` symbol or a power) and some
 * blanks (placeholders). The fixed parts have to line up exactly with the nodes — same kind,
 * matching props, all the way down. Each blank captures the nodes next to it and remembers them,
 * so we can drop those same nodes into a different notation later.
 *
 * How much a blank captures:
 * - A blank on its own (like a fraction's numerator) takes everything on its line — so `x + y`
 *   all goes into one blank.
 * - A blank sitting next to other things (like `blank / blank`) takes a single node plus any
 *   power/subscript stuck to it — so `x` or `x²` (the base and its exponent together), but not
 *   the neighbours beyond that.
 * - The same blank used twice must be filled with the same thing both times.
 *
 * Every node handed in has to be accounted for — this doesn't match a piece in the middle. The
 * caller passes exactly the nodes to check (the selection, or the run found around the cursor).
 *
 * @returns the filled-in blanks (`linkId` → nodes), or `null` if the nodes aren't this notation.
 */
export function matchVariant(
  { nodesJson: pattern }: SwitchNotationVariant,
  nodes: NodeModel[],
): MatchResult {
  const captures = matchLevel(pattern, nodes, new Map());
  return captures ? { captures } : null;
}

function matchLevel(
  pattern: NodeJson[],
  nodes: NodeModel[],
  captures: CapturesMap,
): CapturesMap | null {
  if (pattern.length === 1 && pattern[0].type === "placeholder") {
    const { linkId } = pattern[0].props ?? {};

    if (!linkId) {
      return null;
    }

    if (captures.has(linkId)) {
      return nodesEqual(captures.get(linkId), nodes) ? captures : null;
    }

    return new Map(captures).set(linkId, nodes);
  }

  return segment(pattern, 0, nodes, 0, captures);
}

/**
 * Aligns `pattern[pi..]` against `nodes[ni..]`. Concrete pattern nodes must match 1:1;
 * placeholders-with-siblings capture a base node plus its trailing supsub, trying the longer
 * operand first and backtracking so the supsub is released to a concrete anchor when needed.
 */
function segment(
  pattern: NodeJson[],
  pi: number,
  nodes: NodeModel[],
  ni: number,
  captures: CapturesMap,
): CapturesMap | null {
  if (pi === pattern.length) {
    return ni === nodes.length ? captures : null;
  }

  const patternNode = pattern[pi];

  if (patternNode.type === "placeholder") {
    const { linkId } = patternNode.props ?? {};
    if (!linkId) {
      return null;
    }

    if (captures.has(linkId)) {
      const previous = captures.get(linkId)!;
      const operand = nodes.slice(ni, ni + previous.length);

      if (operand.length !== previous.length || !nodesEqual(previous, operand)) {
        return null;
      }

      return segment(pattern, pi + 1, nodes, ni + previous.length, captures);
    }

    for (let length = operandMaxLength(nodes, ni); length >= 1; length--) {
      const operand = nodes.slice(ni, ni + length);
      const next = segment(
        pattern,
        pi + 1,
        nodes,
        ni + length,
        new Map(captures).set(linkId, operand),
      );

      if (next) {
        return next;
      }
    }
    return null;
  }

  if (ni >= nodes.length) {
    return null;
  }

  const node = nodes[ni];
  if (!compareNodesProps(patternNode, node)) {
    return null;
  }

  const childCaptures = matchLevel(patternNode.children, node.childrenPlease, captures);
  if (!childCaptures) {
    return null;
  }

  return segment(pattern, pi + 1, nodes, ni + 1, childCaptures);
}
/** Length of the operand at `ni`: the base node plus any trailing supsub operators. */
function operandMaxLength(nodes: NodeModel[], ni: number): number {
  if (ni >= nodes.length) {
    return 0;
  }

  let length = 1;
  while (ni + length < nodes.length && isSupSubNode(nodes[ni + length])) {
    length++;
  }

  return length;
}

function nodesEqual(a: NodeModel[] = [], b: NodeModel[] = []): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((node, i) => {
    if (!compareNodesProps(node.toJSON(), b[i])) {
      return false;
    }

    return nodesEqual(node.childrenPlease, b[i].childrenPlease);
  });
}

function compareNodesProps(patternNode: NodeJson, node: NodeModel) {
  if (patternNode.type !== node.type) return false;

  switch (patternNode.type) {
    case "variable": {
      if (node.is(VariableNodeModel)) {
        return compareProps(omit(patternNode.props, ["color"]), omit(node.props, ["color"]));
      }

      return false;
    }

    case "constant": {
      if (node.is(ConstantNodeModel)) {
        return compareProps(omit(patternNode.props, ["color"]), omit(node.props, ["color"]));
      }

      return false;
    }

    default:
      return compareProps(patternNode.props, node.props);
  }
}

function compareProps(patternProps: NodeJson["props"], props: NodeModel["props"]) {
  const nodeProps = toJS(props) as Record<string, unknown>;
  return Object.entries(patternProps).every(([key, value]) => isEqual(nodeProps[key], value));
}
